import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import cloud from 'd3-cloud';
import { cn } from '@/lib/utils';

interface Word {
  text: string;
  value: number;
}

interface WordCloudProps {
  words: Word[];
  width?: number;
  height?: number;
  className?: string;
}

export function WordCloud({ 
  words, 
  width = 500, 
  height = 300,
  className 
}: WordCloudProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !words.length) return;

    // Clear previous content
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Filter and sort words
    const filteredWords = words
      .filter(d => d.text.trim().length > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 50); // Limit number of words

    // Calculate font size scaling
    const maxValue = Math.max(...filteredWords.map(d => d.value));
    const minValue = Math.min(...filteredWords.map(d => d.value));
    const scale = d3.scaleLinear()
      .domain([minValue, maxValue])
      .range([12, 60]); // Adjusted size range

    const layout = cloud()
      .size([width, height])
      .words(filteredWords.map(d => ({ 
        text: d.text, 
        size: scale(d.value),
        value: d.value
      })))
      .padding(8) // Increased padding
      .rotate(() => Math.random() * 90 - 45) // Random rotation between -45 and 45
      .font("sans-serif")
      .fontSize(d => d.size)
      .spiral('rectangular') // More efficient spiral
      .on("end", draw);

    // Add collision detection
    layout.start()
      .on('word', () => {
        // Additional collision checks
        svg.selectAll('text')
          .each(function(this: SVGTextElement, d: any) {
            const current = d3.select(this);
            const bbox = this.getBBox();
            
            svg.selectAll('text').each(function(other: any) {
              if (d === other) return;
              const otherBbox = this.getBBox();
              
              // Check for overlaps
              if (!(bbox.x + bbox.width < otherBbox.x || 
                    bbox.x > otherBbox.x + otherBbox.width || 
                    bbox.y + bbox.height < otherBbox.y || 
                    bbox.y > otherBbox.y + otherBbox.height)) {
                current.remove();
              }
            });
          });
      });

    function draw(words: any[]) {
      const colors = d3.scaleOrdinal()
        .domain(filteredWords.map(d => d.text))
        .range([
          'hsl(var(--primary))',
          'hsl(var(--secondary))',
          'hsl(var(--accent))',
          'hsl(var(--muted-foreground))'
        ]);

      const g = svg.append("g")
        .attr("transform", `translate(${width / 2},${height / 2})`);

      g.selectAll("text")
        .data(words)
        .enter()
        .append("text")
        .style("font-size", d => `${d.size}px`)
        .style("font-family", "sans-serif")
        .style("font-weight", d => d.size > 40 ? 'bold' : 'normal')
        .style("fill", d => colors(d.text))
        .attr("text-anchor", "middle")
        .attr("transform", d => `translate(${d.x},${d.y})rotate(${d.rotate})`)
        .text(d => d.text)
        .style("opacity", 0)
        .transition()
        .delay((_, i) => i * 20) // Staggered appearance
        .duration(500)
        .style("opacity", 1)
        .style("pointer-events", 'auto');

      // Add hover interactions
      g.selectAll('text')
        .on('mouseover', function() {
          d3.select(this).transition().style('opacity', 0.8);
        })
        .on('mouseout', function() {
          d3.select(this).transition().style('opacity', 1);
        });
    }
  }, [words, width, height]);

  return (
    <div className={cn("relative", className)}>
      <svg 
        ref={svgRef} 
        width={width} 
        height={height}
        className="w-full h-full"
        aria-label="Word cloud visualization"
      />
    </div>
  );
}